import type { UserDto } from '../schemas/authSchemas';

interface AccessTokenClaims {
  sub?: string;
  email?: string;
  exp?: number; // Unix timestamp (seconds) — checked against Date.now()
  tenant_id?: string;
  tenant_role?: string;
  is_super_admin?: boolean | string;
  onboarding_completed?: boolean | string;
  email_verified?: boolean | string;
  portal?: string;
  aud?: string | string[];
}

export interface SessionUser extends UserDto {
  tenantRole: string | null;
}

// This portal's expected JWT audience. signcare-auth stamps `aud` (and the
// `portal` claim) with the portal the token was issued for ("studio" |
// "console" | "execute"). Studio's backend only accepts aud="studio".
const EXPECTED_AUDIENCE = 'studio';

function audienceMatches(aud: string | string[] | undefined): boolean {
  if (aud === undefined) {
    // Legacy/unknown tokens with no audience — don't hard-reject here; the
    // backend remains the source of truth and will 401 if it's truly wrong.
    return true;
  }
  return Array.isArray(aud) ? aud.includes(EXPECTED_AUDIENCE) : aud === EXPECTED_AUDIENCE;
}

export function sessionUserFromAccessToken(token: string | null): SessionUser | null {
  if (!token) {
    return null;
  }

  const [, payload] = token.split('.');
  if (!payload) {
    return null;
  }

  try {
    const claims = JSON.parse(decodeBase64Url(payload)) as AccessTokenClaims;
    if (!claims.sub || !claims.email) {
      return null;
    }

    // Reject expired tokens at hydration time — stale tokens from a previous
    // session or a different auth flow (e.g. old direct-login before central
    // auth) should not count as a valid session.
    if (claims.exp && Date.now() / 1000 > claims.exp) {
      return null;
    }

    // Reject tokens minted for a DIFFERENT portal. The sc_refresh cookie is
    // shared across *.signcare.io, so a silent refresh after hopping in from
    // Console/Execute can return a token with aud="console"/"execute". Studio's
    // backend rejects those (401). Treating it as no-session here makes
    // ProtectedRoute run Studio's own OAuth authorize flow, which (silently,
    // via the SSO cookie) mints a proper aud="studio" token instead of looping.
    if (!audienceMatches(claims.aud)) {
      return null;
    }

    return {
      id: claims.sub,
      email: claims.email,
      tenantId: claims.tenant_id ?? null,
      tenantRole: claims.tenant_role ?? null,
      isSuperAdmin: toBoolean(claims.is_super_admin),
      onboardingCompleted: toBoolean(claims.onboarding_completed),
      isEmailVerified: toBoolean(claims.email_verified),
    };
  } catch {
    return null;
  }
}

function decodeBase64Url(value: string): string {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  return atob(padded);
}

function toBoolean(value: boolean | string | undefined): boolean {
  return value === true || value === 'true';
}
